import type { CountryCode } from "@gately/validators";
import { useState } from "react";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRound, MoreHorizontal, Plus, Trash2 } from "lucide-react";

import { Avatar } from "@gately/ui/avatar";
import { Button } from "@gately/ui/button";
import { ConfirmDialog } from "@gately/ui/confirm-dialog";
import { TableShell, Tbody, Td, Th, Thead } from "@gately/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@gately/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@gately/ui/dropdown-menu";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";
import { PhoneInput } from "@gately/ui/phone-input";
import { StatusBadge } from "@gately/ui/status-badge";
import { Switch } from "@gately/ui/switch";
import { toast } from "@gately/ui/toast";
import { isValidPhone, parsePhone, requiredContact } from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { useMe } from "~/lib/use-me";
import { activeOrgType, useTerms } from "~/lib/use-terms";

export const Route = createFileRoute("/_dashboard/equipe")({
  // `?add=1` opens the add-member dialog on arrival — where the sidebar's
  // "Inviter un membre" shortcut sends a unit owner/admin. The URL IS the
  // dialog's open state, so the shortcut works from this page too.
  validateSearch: (search: Record<string, unknown>): { add?: true } =>
    search.add === true || search.add === "1" ? { add: true } : {},
  // Prefetch so useSuspenseQuery has data ready and never suspends mid-navigation.
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(
        orpc.unit.listManageable.queryOptions(),
      ),
      context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
    ]),
  component: EquipePage,
});

/** Same label rule as `units.tsx`: prefer the display name (office units
 *  always have one — the tenant company name), else "Bât. X — Apt Y" for a
 *  residence and a plain "X Y" for an office, where "Apt" would be wrong. */
function unitLabel(
  u: { name?: string | null; building: string | null; number: string },
  isOffice: boolean,
): string {
  if (u.name) return u.name;
  if (isOffice) return u.building ? `${u.building} ${u.number}` : u.number;
  return u.building
    ? `Bât. ${u.building} — Apt ${u.number}`
    : `Apt ${u.number}`;
}

/** "12 mars 2026" — a lease's start date on a « Loué » card. */
function formatLeaseStart(d: Date | string): string {
  return new Date(d).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function EquipePage() {
  const queryClient = useQueryClient();

  // `unit.listManageable` returns every unit the caller owns OR belongs to as
  // a unit-member (any role) — it's the source for pass-creation, which is a
  // broader set than "units whose roster I manage" (owner, or unit-member
  // with role "admin"). We use it only as the *candidate* list, then narrow
  // to `canManage` per unit via `foyer.get` (see `manageableEntries` below).
  const { data: candidates } = useSuspenseQuery(
    orpc.unit.listManageable.queryOptions(),
  );
  const me = useMe();

  const terms = useTerms();
  const isOffice = me.activeOrg?.type === "office";
  // Every « mise en location » affordance hangs off this: an office org must
  // see zero change on this page, so the client gates on the vertical too
  // rather than trusting the server-side flags alone.
  const isResidence = activeOrgType(me) === "residence";

  const foyerResults = useQueries({
    queries: candidates.map((u) =>
      orpc.foyer.get.queryOptions({ input: { unitId: u.id } }),
    ),
  });
  const foyerPending =
    candidates.length > 0 && foyerResults.some((q) => q.isPending);

  type UnitRow = (typeof candidates)[number];
  type FoyerData = NonNullable<(typeof foyerResults)[number]["data"]>;
  interface ManageableEntry {
    unit: UnitRow;
    foyer: FoyerData;
  }

  const manageableEntries = candidates.reduce<ManageableEntry[]>(
    (acc, u, i) => {
      const foyer = foyerResults[i]?.data;
      if (foyer?.canManage) acc.push({ unit: u, foyer });
      return acc;
    },
    [],
  );

  const [selectedUnitId, setSelectedUnitId] = useState<string | undefined>(
    undefined,
  );
  const selectedEntry =
    manageableEntries.find((e) => e.unit.id === selectedUnitId) ??
    manageableEntries[0];

  /**
   * `foyer.get().owner` carries the unit's ACTING resident, which during a
   * lease is the TENANT — labelling that row « Propriétaire » would be a lie.
   * The wire carries no owner/tenant flag, but `canDeclareRental` is only ever
   * true for the legal owner of an unleased residence unit, so it is a sound
   * (if conservative) proof of ownership: true → « Propriétaire », otherwise
   * the neutral « Résident principal », which is accurate for an owner and a
   * tenant alike. Offices never lease, so they keep `terms.ownerLabel` as-is.
   */
  const actingResidentLabel =
    !isResidence || selectedEntry?.foyer.canDeclareRental
      ? terms.ownerLabel
      : "Résident principal";

  const invalidateFoyer = () =>
    queryClient.invalidateQueries({ queryKey: orpc.foyer.get.key() });
  const invalidateLocations = () =>
    queryClient.invalidateQueries({ queryKey: orpc.foyer.listLocations.key() });

  // ── Members: add / promote-demote / remove ─────────────────────────────
  // Held in the URL rather than in state — see `validateSearch` above.
  const addMemberOpen = Route.useSearch().add === true;
  const navigate = Route.useNavigate();
  const setAddMemberOpen = (open: boolean) =>
    void navigate({ search: open ? { add: true } : {}, replace: true });
  const [memberName, setMemberName] = useState("");
  const [memberPhone, setMemberPhone] = useState("");
  const [memberCountry, setMemberCountry] = useState<CountryCode>("MA");
  const [memberEmail, setMemberEmail] = useState("");
  const [memberIsAdmin, setMemberIsAdmin] = useState(false);

  const resetAddMemberForm = () => {
    setMemberName("");
    setMemberPhone("");
    setMemberCountry("MA");
    setMemberEmail("");
    setMemberIsAdmin(false);
  };

  const addMember = useMutation(
    orpc.foyer.addMember.mutationOptions({
      onSuccess: async (data) => {
        toast.success(
          data.reused
            ? `Compte existant « ${data.existingName} » ajouté à l'équipe.`
            : "Membre ajouté.",
        );
        resetAddMemberForm();
        setAddMemberOpen(false);
        await invalidateFoyer();
      },
      onError: (err) =>
        toast.error(err.message || "Impossible d'ajouter ce membre."),
    }),
  );

  const setMemberRole = useMutation(
    orpc.foyer.setMemberRole.mutationOptions({
      onSuccess: async () => {
        toast.success("Rôle mis à jour.");
        await invalidateFoyer();
      },
      onError: (err) =>
        toast.error(err.message || "Échec de la mise à jour du rôle."),
    }),
  );

  const removeMember = useMutation(
    orpc.foyer.removeMember.mutationOptions({
      onSuccess: async () => {
        toast.success("Membre retiré.");
        await invalidateFoyer();
      },
      onError: (err) => toast.error(err.message || "Échec du retrait."),
    }),
  );

  const memberPhoneTouched = memberPhone.trim().length > 0;
  const memberPhoneValid =
    !memberPhoneTouched || isValidPhone(memberPhone, memberCountry);
  const memberEmailTouched = memberEmail.trim().length > 0;
  const memberEmailValid =
    !memberEmailTouched ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(memberEmail.trim());
  // Required identifier per vertical (same rule the server enforces):
  // residence → phone, office → email. The other one is an optional extra.
  const needContact = requiredContact(activeOrgType(me));
  const memberHasRequiredContact =
    needContact === "phone" ? memberPhoneTouched : memberEmailTouched;
  const canSubmitMember =
    memberName.trim().length > 0 &&
    memberHasRequiredContact &&
    memberPhoneValid &&
    memberEmailValid;

  function submitAddMember(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitMember || !selectedEntry) return;
    addMember.mutate({
      unitId: selectedEntry.unit.id,
      name: memberName.trim(),
      phone: memberPhoneTouched
        ? (parsePhone(memberPhone, memberCountry) ?? undefined)
        : undefined,
      email: memberEmailTouched ? memberEmail.trim() : undefined,
      role: memberIsAdmin ? "admin" : "member",
    });
  }

  // ── Mise en location (residence orgs only) ──────────────────────────────
  // A rented-out unit leaves `unit.listManageable` for the length of the lease
  // — its owner is no longer part of the acting household — so `occupancy.mine`
  // is the owner's ONLY handle on it, and the « Loué » cards below come from
  // there rather than from the roster candidates.
  const rentedOutQuery = useQuery({
    ...orpc.occupancy.mine.queryOptions(),
    enabled: isResidence,
  });
  const rentedOut = isResidence ? (rentedOutQuery.data ?? []) : [];
  // Swallowing this would make the owner's leases silently vanish — and with
  // them the only way to end one — so a failed load says so in the section's
  // place instead of rendering nothing.
  const rentedOutFailed = isResidence && rentedOutQuery.isError;

  // Declaring or ending a lease moves a unit between the roster list and the
  // « Loué » cards, and flips the caller's own household on `me.get`.
  const invalidateRental = () =>
    Promise.all([
      queryClient.invalidateQueries({
        queryKey: orpc.unit.listManageable.key(),
      }),
      queryClient.invalidateQueries({ queryKey: orpc.occupancy.mine.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.foyer.get.key() }),
      queryClient.invalidateQueries({ queryKey: orpc.me.get.key() }),
    ]);

  const [declareOpen, setDeclareOpen] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [tenantCountry, setTenantCountry] = useState<CountryCode>("MA");
  const [tenantEmail, setTenantEmail] = useState("");

  const resetDeclareForm = () => {
    setTenantName("");
    setTenantPhone("");
    setTenantCountry("MA");
    setTenantEmail("");
  };

  const declareRental = useMutation(
    orpc.occupancy.declare.mutationOptions({
      onSuccess: async () => {
        toast.success("Unité mise en location.");
        resetDeclareForm();
        setDeclareOpen(false);
        // The unit is about to leave `manageableEntries`; drop the tab
        // selection so it falls back to a unit that still exists.
        setSelectedUnitId(undefined);
        await invalidateRental();
      },
      onError: (err) =>
        toast.error(
          err.message || "Impossible de mettre cette unité en location.",
        ),
    }),
  );

  // The confirm dialog's subject — null when closed.
  const [endTarget, setEndTarget] = useState<{
    unitId: string;
    label: string;
    tenantName: string;
  } | null>(null);

  const endRental = useMutation(
    orpc.occupancy.end.mutationOptions({
      // Both arms clear the confirm (ConfirmDialog's contract): closing only on
      // success would strand the dialog open over a stale question, with live
      // buttons and no sign of what went wrong.
      onSuccess: async () => {
        toast.success("Location terminée.");
        setEndTarget(null);
        await invalidateRental();
      },
      onError: (err) => {
        setEndTarget(null);
        toast.error(err.message || "Impossible de terminer cette location.");
      },
    }),
  );

  // The dialog only ever renders in a residence, where `requiredContact` is
  // always "phone" — hence the fixed rule here rather than the add-member
  // dialog's per-vertical field ordering.
  const tenantPhoneTouched = tenantPhone.trim().length > 0;
  const tenantPhoneValid =
    !tenantPhoneTouched || isValidPhone(tenantPhone, tenantCountry);
  const tenantEmailTouched = tenantEmail.trim().length > 0;
  const tenantEmailValid =
    !tenantEmailTouched ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tenantEmail.trim());
  const canSubmitTenant =
    tenantName.trim().length > 0 &&
    tenantPhoneTouched &&
    tenantPhoneValid &&
    tenantEmailValid;

  function submitDeclareRental(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmitTenant || !selectedEntry?.foyer.canDeclareRental) return;
    declareRental.mutate({
      unitId: selectedEntry.unit.id,
      name: tenantName.trim(),
      phone: parsePhone(tenantPhone, tenantCountry) ?? undefined,
      email: tenantEmailTouched ? tenantEmail.trim() : undefined,
    });
  }

  // ── Locations (office orgs only) ────────────────────────────────────────
  const locationsQuery = useQuery({
    ...orpc.foyer.listLocations.queryOptions({
      input: { unitId: selectedEntry?.unit.id ?? "" },
    }),
    enabled: isOffice && !!selectedEntry,
  });

  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [locationLabel, setLocationLabel] = useState("");
  const [locationDirections, setLocationDirections] = useState("");

  const addLocation = useMutation(
    orpc.foyer.addLocation.mutationOptions({
      onSuccess: async () => {
        toast.success("Emplacement ajouté.");
        setLocationLabel("");
        setLocationDirections("");
        setAddLocationOpen(false);
        await invalidateLocations();
      },
      onError: (err) =>
        toast.error(err.message || "Impossible d'ajouter cet emplacement."),
    }),
  );

  const removeLocation = useMutation(
    orpc.foyer.removeLocation.mutationOptions({
      onSuccess: async () => {
        toast.success("Emplacement supprimé.");
        await invalidateLocations();
      },
      onError: (err) => toast.error(err.message || "Échec de la suppression."),
    }),
  );

  function submitAddLocation(e: React.FormEvent) {
    e.preventDefault();
    if (!locationLabel.trim() || !selectedEntry) return;
    addLocation.mutate({
      unitId: selectedEntry.unit.id,
      label: locationLabel.trim(),
      directions: locationDirections.trim() || undefined,
    });
  }

  return (
    <div className="px-7 py-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {terms.teamNav}
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Gérez les membres{" "}
          {isOffice ? "de votre entreprise" : "de votre foyer"} et leurs accès.
        </p>
      </div>

      {/* ── Rented-out units (residence orgs only) ──────────────────────── */}
      {rentedOutFailed ? (
        <p className="text-destructive mt-6 text-sm">
          Impossible de charger vos unités en location.
        </p>
      ) : rentedOut.length > 0 ? (
        <div className="mt-6">
          <h2 className="text-lg font-semibold">Unités en location</h2>
          <div className="bg-card mt-3 divide-y rounded-xl border">
            {rentedOut.map((r) => (
              <div
                key={r.occupancyId}
                className="flex flex-wrap items-center justify-between gap-4 px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{r.unitLabel}</span>
                    <StatusBadge tone="warning" dot={false}>
                      Loué
                    </StatusBadge>
                  </div>
                  <div className="text-muted-foreground mt-0.5 text-sm">
                    Locataire : {r.tenantName}
                  </div>
                  <div className="text-muted-foreground text-sm">
                    Depuis le {formatLeaseStart(r.startedAt)}
                  </div>
                </div>
                <Button
                  variant="destructive"
                  onClick={() =>
                    setEndTarget({
                      unitId: r.unitId,
                      label: r.unitLabel,
                      tenantName: r.tenantName,
                    })
                  }
                >
                  Terminer la location
                </Button>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {foyerPending ? (
        <p className="text-muted-foreground mt-6 text-sm">Chargement…</p>
      ) : manageableEntries.length === 0 ? (
        // An owner who has rented out every unit they hold manages no roster —
        // the « Loué » cards above already say why, so don't also say "nothing".
        // Same when those cards failed to load: "aucune équipe" would then be a
        // claim we cannot actually make.
        rentedOut.length > 0 || rentedOutFailed ? null : (
          <p className="text-muted-foreground mt-6 text-sm">
            Aucune équipe à gérer.
          </p>
        )
      ) : (
        <>
          {manageableEntries.length > 1 ? (
            <div className="mt-6 flex gap-6 border-b">
              {manageableEntries.map((e) => (
                <button
                  key={e.unit.id}
                  type="button"
                  onClick={() => setSelectedUnitId(e.unit.id)}
                  className={`relative -mb-px pb-3 text-sm font-medium transition-colors ${
                    selectedEntry?.unit.id === e.unit.id
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {unitLabel(e.unit, isOffice)}
                  {selectedEntry?.unit.id === e.unit.id ? (
                    <span className="bg-brand absolute right-0 -bottom-px left-0 h-0.5 rounded-full" />
                  ) : null}
                </button>
              ))}
            </div>
          ) : null}

          {selectedEntry ? (
            <>
              {/* ── Members ────────────────────────────────────────────── */}
              <div className="mt-6 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold">Membres</h2>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {unitLabel(selectedEntry.unit, isOffice)}
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  {/* `canDeclareRental` is already residence-only server-side;
                      `isResidence` keeps that guarantee readable here. */}
                  {isResidence && selectedEntry.foyer.canDeclareRental ? (
                    <Button
                      variant="outline"
                      onClick={() => setDeclareOpen(true)}
                    >
                      <KeyRound className="size-4" />
                      Mettre en location
                    </Button>
                  ) : null}
                  <Button onClick={() => setAddMemberOpen(true)}>
                    <Plus className="size-4" />
                    Ajouter
                  </Button>
                </div>
              </div>

              {selectedEntry.foyer.owner ||
              selectedEntry.foyer.members.length > 0 ? (
                <TableShell className="mt-4">
                  <Thead>
                    <tr>
                      <Th>Nom</Th>
                      <Th>Contact</Th>
                      <Th>Rôle</Th>
                      <Th>
                        <span className="sr-only">Actions</span>
                      </Th>
                    </tr>
                  </Thead>
                  <Tbody>
                    {selectedEntry.foyer.owner ? (
                      <tr>
                        <Td>
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar name={selectedEntry.foyer.owner.name} />
                            <div className="min-w-0">
                              <div className="truncate font-medium">
                                {selectedEntry.foyer.owner.name}
                              </div>
                              {selectedEntry.foyer.owner.userId ===
                              me.user.id ? (
                                <div className="text-muted-foreground text-xs">
                                  Vous
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </Td>
                        <Td className="text-muted-foreground max-w-0 truncate text-sm">
                          {selectedEntry.foyer.owner.phone ??
                            selectedEntry.foyer.owner.email ??
                            "—"}
                        </Td>
                        <Td>
                          <StatusBadge tone="brand" dot={false}>
                            {actingResidentLabel}
                          </StatusBadge>
                        </Td>
                        <Td />
                      </tr>
                    ) : null}
                    {selectedEntry.foyer.members.map((m) => (
                      <tr key={m.userId}>
                        <Td>
                          <div className="flex min-w-0 items-center gap-3">
                            <Avatar name={m.name} />
                            <div className="min-w-0">
                              <div className="truncate font-medium">
                                {m.name}
                              </div>
                              {m.userId === me.user.id ? (
                                <div className="text-muted-foreground text-xs">
                                  Vous
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </Td>
                        <Td className="text-muted-foreground max-w-0 truncate text-sm">
                          {m.phone ?? m.email ?? "—"}
                        </Td>
                        <Td>
                          <StatusBadge
                            tone={m.role === "admin" ? "brand" : "info"}
                            dot={false}
                          >
                            {m.role === "admin" ? "Admin" : "Membre"}
                          </StatusBadge>
                        </Td>
                        <Td>
                          <div className="flex justify-end">
                            <DropdownMenu>
                              <DropdownMenuTrigger
                                aria-label={`Actions pour ${m.name}`}
                                className="hover:bg-muted flex size-8 items-center justify-center rounded-lg outline-none"
                              >
                                <MoreHorizontal className="size-4" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {m.role === "admin" ? (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setMemberRole.mutate({
                                        unitId: selectedEntry.unit.id,
                                        userId: m.userId,
                                        role: "member",
                                      })
                                    }
                                  >
                                    Rétrograder en membre
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setMemberRole.mutate({
                                        unitId: selectedEntry.unit.id,
                                        userId: m.userId,
                                        role: "admin",
                                      })
                                    }
                                  >
                                    Promouvoir administrateur
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  variant="destructive"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Retirer ${m.name} de l'équipe ?`,
                                      )
                                    ) {
                                      removeMember.mutate({
                                        unitId: selectedEntry.unit.id,
                                        userId: m.userId,
                                      });
                                    }
                                  }}
                                >
                                  <Trash2 className="size-4" />
                                  Retirer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </Td>
                      </tr>
                    ))}
                  </Tbody>
                </TableShell>
              ) : (
                <p className="text-muted-foreground mt-4 text-sm">
                  Aucun membre pour l'instant.
                </p>
              )}

              {/* ── Locations (office orgs only) ──────────────────────── */}
              {isOffice ? (
                <div className="mt-10">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h2 className="text-lg font-semibold">Emplacements</h2>
                      <p className="text-muted-foreground mt-1 text-sm">
                        Affiché sur les pass des visiteurs (« Où nous trouver
                        »).
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => setAddLocationOpen(true)}
                    >
                      <Plus className="size-4" />
                      Ajouter
                    </Button>
                  </div>

                  {locationsQuery.isPending ? (
                    <p className="text-muted-foreground mt-4 text-sm">
                      Chargement…
                    </p>
                  ) : (locationsQuery.data?.length ?? 0) === 0 ? (
                    <p className="text-muted-foreground mt-4 text-sm">
                      Aucun emplacement pour l'instant.
                    </p>
                  ) : (
                    <div className="bg-card mt-4 divide-y rounded-xl border">
                      {(locationsQuery.data ?? []).map((loc) => (
                        <div
                          key={loc.id}
                          className="flex items-start justify-between gap-4 px-4 py-3"
                        >
                          <div className="min-w-0">
                            <div className="font-medium">{loc.label}</div>
                            {loc.directions ? (
                              <div className="text-muted-foreground mt-0.5 text-sm">
                                {loc.directions}
                              </div>
                            ) : null}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={`Supprimer ${loc.label}`}
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Supprimer l'emplacement « ${loc.label} » ?`,
                                )
                              ) {
                                removeLocation.mutate({ id: loc.id });
                              }
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </>
      )}

      {/* ── Add member dialog ───────────────────────────────────────────── */}
      <Dialog
        open={addMemberOpen}
        onOpenChange={(open) => {
          setAddMemberOpen(open);
          if (!open) resetAddMemberForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un membre</DialogTitle>
            <DialogDescription>
              {needContact === "email"
                ? "Renseignez l'e-mail professionnel du membre — le téléphone est optionnel."
                : "Renseignez le numéro de téléphone du membre — l'e-mail est optionnel."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAddMember} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="member-name">Nom complet</Label>
              <Input
                id="member-name"
                value={memberName}
                onChange={(e) => setMemberName(e.target.value)}
                placeholder="Fatima Zahra El Amrani"
                required
              />
            </div>
            {/* Required identifier first; the optional one follows. */}
            {(() => {
              const phoneField = (
                <div key="phone" className="flex flex-col gap-2">
                  <Label htmlFor="member-phone">
                    {needContact === "phone"
                      ? "Téléphone"
                      : "Téléphone (optionnel)"}
                  </Label>
                  <PhoneInput
                    id="member-phone"
                    country={memberCountry}
                    onCountryChange={setMemberCountry}
                    value={memberPhone}
                    onChange={setMemberPhone}
                    invalid={memberPhoneTouched && !memberPhoneValid}
                    required={needContact === "phone"}
                  />
                  {memberPhoneTouched && !memberPhoneValid ? (
                    <p className="text-destructive text-sm">
                      Numéro de téléphone invalide.
                    </p>
                  ) : null}
                </div>
              );
              const emailField = (
                <div key="email" className="flex flex-col gap-2">
                  <Label htmlFor="member-email">
                    {needContact === "email" ? "E-mail" : "E-mail (optionnel)"}
                  </Label>
                  <Input
                    id="member-email"
                    type="email"
                    value={memberEmail}
                    onChange={(e) => setMemberEmail(e.target.value)}
                    placeholder="prenom.nom@entreprise.ma"
                    aria-invalid={memberEmailTouched && !memberEmailValid}
                    required={needContact === "email"}
                  />
                  {memberEmailTouched && !memberEmailValid ? (
                    <p className="text-destructive text-sm">
                      Adresse e-mail invalide.
                    </p>
                  ) : null}
                </div>
              );
              return needContact === "email"
                ? [emailField, phoneField]
                : [phoneField, emailField];
            })()}
            {!memberHasRequiredContact ? (
              <p className="text-destructive text-sm">
                {needContact === "email"
                  ? "E-mail requis."
                  : "Numéro de téléphone requis."}
              </p>
            ) : null}
            <div className="flex items-center justify-between rounded-lg border px-3 py-2.5">
              <Label htmlFor="member-admin" className="cursor-pointer">
                Administrateur
              </Label>
              <Switch
                id="member-admin"
                checked={memberIsAdmin}
                onCheckedChange={setMemberIsAdmin}
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddMemberOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={addMember.isPending || !canSubmitMember}
              >
                {addMember.isPending ? "Ajout…" : "Ajouter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Mettre en location dialog (residence orgs only) ─────────────── */}
      <Dialog
        open={declareOpen}
        onOpenChange={(open) => {
          setDeclareOpen(open);
          if (!open) resetDeclareForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mettre en location</DialogTitle>
            <DialogDescription>
              Renseignez le numéro de téléphone du locataire — l'e-mail est
              optionnel.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitDeclareRental} className="flex flex-col gap-4">
            <p className="bg-warning-tint text-warning rounded-lg px-3 py-2.5 text-sm">
              Pendant la location, vous et votre foyer n'aurez plus accès aux
              visites et passes de cette unité. Vos passes actifs seront
              révoqués.
            </p>
            <div className="flex flex-col gap-2">
              <Label htmlFor="tenant-name">Nom complet</Label>
              <Input
                id="tenant-name"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                placeholder="Fatima Zahra El Amrani"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="tenant-phone">Téléphone</Label>
              <PhoneInput
                id="tenant-phone"
                country={tenantCountry}
                onCountryChange={setTenantCountry}
                value={tenantPhone}
                onChange={setTenantPhone}
                invalid={tenantPhoneTouched && !tenantPhoneValid}
                required
              />
              {tenantPhoneTouched && !tenantPhoneValid ? (
                <p className="text-destructive text-sm">
                  Numéro de téléphone invalide.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="tenant-email">E-mail (optionnel)</Label>
              <Input
                id="tenant-email"
                type="email"
                value={tenantEmail}
                onChange={(e) => setTenantEmail(e.target.value)}
                placeholder="prenom.nom@exemple.ma"
                aria-invalid={tenantEmailTouched && !tenantEmailValid}
              />
              {tenantEmailTouched && !tenantEmailValid ? (
                <p className="text-destructive text-sm">
                  Adresse e-mail invalide.
                </p>
              ) : null}
            </div>
            {!tenantPhoneTouched ? (
              <p className="text-destructive text-sm">
                Numéro de téléphone requis.
              </p>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeclareOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={declareRental.isPending || !canSubmitTenant}
              >
                {declareRental.isPending ? "Enregistrement…" : "Confirmer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Terminer la location confirm ────────────────────────────────── */}
      {/* `description` is a node so the lease being ended can be named in bold
          above the ruling sentence; `DialogDescription` renders a <p>, hence
          the block <span> rather than a nested paragraph. */}
      <ConfirmDialog
        open={endTarget !== null}
        onOpenChange={(open) => {
          if (!open) setEndTarget(null);
        }}
        icon={<KeyRound className="size-5" strokeWidth={1.85} />}
        title="Terminer la location"
        description={
          <>
            <b className="text-foreground/80 font-semibold">
              {endTarget?.label}
            </b>{" "}
            — locataire : {endTarget?.tenantName}
            <span className="mt-2 block">
              Le locataire et son foyer perdront immédiatement l'accès à
              l'unité. Leurs passes actifs seront révoqués.
            </span>
          </>
        }
        confirmLabel="Terminer la location"
        pendingLabel="Traitement…"
        destructive
        pending={endRental.isPending}
        onConfirm={() => {
          if (endTarget) endRental.mutate({ unitId: endTarget.unitId });
        }}
      />

      {/* ── Add location dialog ─────────────────────────────────────────── */}
      <Dialog
        open={addLocationOpen}
        onOpenChange={(open) => {
          setAddLocationOpen(open);
          if (!open) {
            setLocationLabel("");
            setLocationDirections("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un emplacement</DialogTitle>
            <DialogDescription>
              Affiché sur les pass des visiteurs (« Où nous trouver »).
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAddLocation} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="location-label">Nom</Label>
              <Input
                id="location-label"
                value={locationLabel}
                onChange={(e) => setLocationLabel(e.target.value)}
                placeholder="Accueil, Bâtiment B, 3ᵉ étage…"
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="location-directions">
                Indications (facultatif)
              </Label>
              <textarea
                id="location-directions"
                value={locationDirections}
                onChange={(e) => setLocationDirections(e.target.value)}
                maxLength={280}
                rows={3}
                className="border-input placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 dark:bg-input/30 w-full rounded-lg border bg-transparent px-3 py-2 text-base shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] md:text-sm"
                placeholder="Ex. : porte à droite de l'entrée principale."
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setAddLocationOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={addLocation.isPending || !locationLabel.trim()}
              >
                {addLocation.isPending ? "Ajout…" : "Ajouter"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
